/**
 * The decisions behind the acceptance gate, kept out of the router and out of React so they can
 * be read — and tested — on their own.
 *
 * The gate itself is three lines in `protectedLayout.beforeLoad` (app/router.tsx): ask the
 * server what is still pending, and if anything is, redirect to `/legal-acceptance`. Everything
 * that makes that safe lives here: which URL is exempt (the gate itself, or it redirects to
 * itself forever), what «pending» actually means, and where a person is returned afterwards.
 */
import type { PendingLegal, PendingLegalDocument } from '@/entities/legal/api';
import { isLegalKind, type LegalKind } from '@/entities/legal/api';

/**
 * The blocking step's own path.
 *
 * Not under `/legal/` on purpose: `/legal/$document` is the public reader, and a static child of
 * a dynamic segment in a different branch of the tree is the kind of route collision that only
 * shows up when somebody opens the wrong screen in production.
 */
export const LEGAL_ACCEPT_PATH = '/legal-acceptance';

/**
 * The document the gate must show next, or null when the app may proceed.
 *
 * `all_accepted` and an empty `pending` are treated as the same answer, and the empty list wins
 * over a false `all_accepted`: with nothing to display there is no screen to show and no hash to
 * send, so blocking would strand the person on a page with a button that cannot do anything. The
 * server still refuses the order with `legal_acceptance_required` if it really did mean to block,
 * which is the safety net that makes «пропустить» here the safe direction.
 */
export function nextPendingDocument(
  pending: PendingLegal | undefined,
): PendingLegalDocument | null {
  if (pending === undefined) return null;
  if (pending.all_accepted) return null;
  return pending.pending[0] ?? null;
}

/** True when the app must not be shown until something is accepted. */
export function needsAcceptance(pending: PendingLegal | undefined): boolean {
  return nextPendingDocument(pending) !== null;
}

/**
 * Whether the gate applies to this path.
 *
 * Only the acceptance screen itself is exempt. The public reader at `/legal/$document` needs no
 * exemption — it hangs off the root route, outside the protected layout, so a person under the
 * gate can still open and read the documents from the acceptance screen.
 */
export function isGateExempt(pathname: string): boolean {
  return pathname === LEGAL_ACCEPT_PATH;
}

/**
 * Where to send somebody back after they accept.
 *
 * Same rule as the login screen's `redirect`: relative paths only. A `redirect` that could point
 * at another origin turns a screen everybody is forced through into an open redirect. The gate's
 * own path is refused too — returning there would re-run the gate and look like a hang.
 */
export function safeReturnTo(target: string | undefined): string | undefined {
  if (typeof target !== 'string') return undefined;
  // `//evil.example` is protocol-relative: it starts with `/` and still leaves the site.
  if (!target.startsWith('/') || target.startsWith('//')) return undefined;
  if (target === LEGAL_ACCEPT_PATH || target.startsWith(`${LEGAL_ACCEPT_PATH}?`)) return undefined;
  return target;
}

/**
 * The document slugs the reader accepts, mapped to the kinds the API uses.
 *
 * `offer` and `privacy` are the slugs the registration form has always linked to, so both stay
 * alive — the privacy policy is not a separate document on this backend, it is the second half
 * of the offer, and pointing its link at that document is what the consent text has always
 * meant. Raw kinds work too, so a link copied out of the API docs opens the right page.
 */
const SLUG_TO_KIND: Record<string, LegalKind> = {
  offer: 'offer_and_privacy_policy',
  privacy: 'offer_and_privacy_policy',
  'personal-data': 'personal_data_processing',
  checklist: 'order_checklist',
};

export function documentKindFromSlug(slug: string): LegalKind | undefined {
  const known = SLUG_TO_KIND[slug];
  if (known !== undefined) return known;
  return isLegalKind(slug) ? slug : undefined;
}

/** The canonical slug for a kind — used by the profile's reading links. */
export function slugForKind(kind: LegalKind): string {
  if (kind === 'personal_data_processing') return 'personal-data';
  if (kind === 'order_checklist') return 'checklist';
  return 'offer';
}

/**
 * A fallback title for the reader while the text is loading, and for a document the server will
 * not give us. The real title always comes from the response — this is scaffolding, not a copy
 * of the document names.
 */
export function fallbackTitle(kind: LegalKind): string {
  if (kind === 'personal_data_processing') return 'Обработка персональных данных';
  if (kind === 'order_checklist') return 'Чек-лист перед заказом';
  return 'Оферта и политика конфиденциальности';
}
